import { useState, useEffect, useRef, useMemo } from "react";
import { T } from "../theme";
import { Button, Badge } from "./fmxkit";
import { resolveEndpoint, FMX_ASSIGNMENT_FIELDS, getModeCapabilities, supportsMode } from "../fmxEndpoints";
import { transformRowToPayload, buildIdCache, fetchAllRecords } from "../fmxTransform";
import { deriveFieldMap, deriveLookupFields, getCustomFieldsPayloadKey } from "../fmxFieldMetadata";
import { getBaseSchemaType } from "../schemas";
import { fetchPostOptions } from "../fmxSync";
import { fmxBatchAll, fmxErrorMessage, claudeFetch, parseClaudeText } from "../apiClient";
import { downloadCSV } from "../utils";
import { getAllDependencyCaches, savePush, markPushUndone, saveMappings, saveRule, saveDataPatterns, completeImport, updateImportPushOutcome } from "../db";
import { validatePayload, applyDefaults } from "../fmxValidation";
import { executePushUndo } from "../fmxUndo";

// StepExport — "Push to FMX" step redesign (design_handoff_wizard_v2 step 4).
// One screen, three phases over the real push engine (formerly FMXPushModal):
//   ready   — status strip, hold-back card for rows still flagged in Validate,
//             destination cards (download the cleaned file / push via the API)
//   pushing — live progress card + streaming per-record results with
//             "View in FMX" links the moment each record lands
//   done    — result tiles, failed-row triage with per-row Retry/Skip,
//             a successful-rows ledger, and a summary with Finish.
// Skipped rows from Validate stay in the file and are never pushed; flagged
// rows can be held back to flagged_rows.csv and pushed later.

const SECTION_LABEL = { font: `700 11px/16px ${T.fontSans}`, textTransform: "uppercase", letterSpacing: "0.07em" };
const PILL = {
  font: `600 9.5px/14px ${T.fontSans}`, textTransform: "uppercase", letterSpacing: "0.04em",
  background: T.white, borderRadius: T.radiusPill, padding: "0 7px", flexShrink: 0,
};
const CARD = { background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard };

const rowName = (row, ri) => row?.["Name"] || row?.["Title"] || row?.["Tag"] || row?.["Email"] || `Row ${ri + 1}`;

const failKind = msg => {
  const m = String(msg || "").toLowerCase();
  if (/already exist|duplicate/.test(m)) return "duplicate";
  if (/not found|no existing record|doesn't exist|does not exist|missing .*(id|reference)|unresolved/.test(m)) return "dependency";
  return "data";
};

const failHint = kind => kind === "dependency"
  ? "The reference points at a record FMX doesn't have — import that entity first, or fix the value and retry."
  : kind === "duplicate"
    ? "A record with this value is already on the site — fix the value, or use update mode to modify the existing record."
    : "Fix the value and retry — retrying never duplicates rows that already made it.";

export default function StepExport({
  schemaType,
  mappedRows,
  mappedHeaders,
  allFields,
  handleExport,
  mapping,
  transformRules,
  projectId,
  onImportComplete,
  selectedProject,
  customFieldIdMap,
  customFieldMetadata,
  systemFieldMetadata,
  fileInfo,
  cellErrors,
  skippedRows,
  onFailedCount,
  nextEntity,
  onFinish,
}) {
  const [phase, setPhase] = useState("ready");
  const [holdBack, setHoldBack] = useState(false);
  const [pushMode, setPushMode] = useState("create");
  const [ackIrreversible, setAckIrreversible] = useState(false);
  const [prepMsg, setPrepMsg] = useState("");
  const [results, setResults] = useState([]); // [{ ri, name, status, id, error, kind }]
  const [doneAt, setDoneAt] = useState(null);
  const [durationMs, setDurationMs] = useState(0);
  const [retrying, setRetrying] = useState({}); // ri -> true while a retry runs
  const [dismissedFails, setDismissedFails] = useState(new Set());
  const [undoState, setUndoState] = useState(null); // { running, done, total } | { result }
  const cancelledRef = useRef(false);
  const startRef = useRef(0);

  const siteUrl = selectedProject?.fmx_site_url || "";
  const fmxModules = selectedProject?.fmx_modules || {};
  const hasCreds = !!selectedProject?.fmx_credentials && !!projectId;
  const creds = useMemo(() => ({ projectId }), [projectId]);

  const modeCaps = getModeCapabilities(schemaType);
  const availableModes = ["create", "update", "delete"].filter(m => modeCaps[m]);
  useEffect(() => { if (!supportsMode(schemaType, pushMode)) setPushMode(availableModes[0] || "create"); }, [schemaType]);
  useEffect(() => { setAckIrreversible(false); }, [pushMode]);
  const isIrreversible = (pushMode === "create" && !supportsMode(schemaType, "delete")) || pushMode === "delete";

  // ---- row sets -------------------------------------------------------------
  const totalRows = mappedRows.length;
  const skipped = skippedRows || new Set();
  const flaggedIdx = useMemo(() => {
    const s = new Set();
    for (const [key, v] of Object.entries(cellErrors || {})) {
      if (v !== "error" && v !== "dep_error") continue;
      const ri = parseInt(key.slice(0, key.indexOf("-")), 10);
      if (!isNaN(ri) && !skipped.has(ri)) s.add(ri);
    }
    return [...s].sort((a, b) => a - b);
  }, [cellErrors, skipped]);

  const pushIdx = useMemo(() => {
    const out = [];
    for (let i = 0; i < totalRows; i++) {
      if (skipped.has(i)) continue;
      if (holdBack && flaggedIdx.includes(i)) continue;
      out.push(i);
    }
    return out;
  }, [totalRows, skipped, holdBack, flaggedIdx]);
  const pushTotal = pushIdx.length;

  const downloadFlagged = () => {
    const rows = flaggedIdx.map(ri => mappedRows[ri]);
    if (rows.length) downloadCSV("flagged_rows.csv", mappedHeaders, rows);
  };

  // ---- persistence (mappings, rules, patterns, import record) ----------------
  const persistedRef = useRef(false);
  const importIdRef = useRef(null);
  const runPersistence = async () => {
    if (persistedRef.current) return;
    persistedRef.current = true;
    try {
        let referenceValues = [];
        if (schemaType === "Building" || schemaType === "Equipment Type") {
          const uniqueNames = [...new Set(mappedRows.map(r => r["Name"]).filter(Boolean))];
          referenceValues = uniqueNames.map(v => ({ fieldName: "Name", value: v }));
        } else {
          for (const field of allFields.filter(f => f.crossSheet)) {
            const uniqueVals = [...new Set(mappedRows.map(r => r[field.name]).filter(Boolean))];
            uniqueVals.forEach(v => referenceValues.push({ fieldName: field.name, value: v }));
          }
        }
        const fieldSamples = {};
        for (const field of allFields) {
          const samples = [...new Set(mappedRows.map(r => r[field.name]).filter(v => v !== undefined && v !== null && v !== ""))].slice(0, 10);
          if (samples.length > 0) fieldSamples[field.name] = samples;
        }
        let fieldPatterns = [];
        if (Object.keys(fieldSamples).length > 0) {
          try {
            const timeout = new Promise((_, reject) => setTimeout(() => reject(new Error("timeout")), 10000));
            const data = await Promise.race([
              claudeFetch({
                max_tokens: 1000,
                messages: [{
                  role: "user",
                  content: `Analyze these data samples from an FMX ${schemaType} import. For each field, give a ONE sentence pattern hint describing the data format. Return ONLY a JSON object where keys are field names and values are pattern hint strings. Data samples: ${JSON.stringify(fieldSamples)}`,
                }],
              }),
              timeout,
            ]);
            const hints = JSON.parse(parseClaudeText(data) || "{}");
            fieldPatterns = Object.entries(fieldSamples).map(([fmxField, sampleValues]) => ({ fmxField, sampleValues, patternHint: hints[fmxField] || null }));
          } catch { /* pattern hints are best-effort */ }
        }
        const saves = [saveMappings(schemaType, mapping)];
        Object.entries(transformRules || {}).forEach(([fieldName, rule]) => {
          if (rule?.instruction && rule?.code) saves.push(saveRule(schemaType, fieldName, rule.instruction, rule.code));
        });
        if (fieldPatterns.length > 0) saves.push(saveDataPatterns(schemaType, fieldPatterns));
        const [importId] = await Promise.all([
          projectId ? completeImport(projectId, schemaType, mappedRows.length, mapping, referenceValues, mappedRows, null, fileInfo?.name || null) : Promise.resolve(null),
          ...saves,
        ]);
        if (importId && importId !== true) importIdRef.current = importId;
        if (onImportComplete) onImportComplete({ schemaType, referenceValues });
    } catch { /* non-fatal */ }
  };

  // Record the actual push outcome on the import record so the Migration grid
  // can tell pushed / partially-pushed / mapped-only apart. Called after the
  // push loop and again on Finish so retried rows flip partial → pushed.
  const persistPushOutcome = async entries => {
    if (!importIdRef.current || !entries?.length) return;
    const pushed = entries.filter(e => e.status !== "failed").length;
    try {
      await updateImportPushOutcome(importIdRef.current, { pushedCount: pushed, failedCount: entries.length - pushed });
    } catch { /* non-fatal */ }
  };

  const handleDownload = format => {
    handleExport(format);
    runPersistence();
  };

  // ---- push engine ------------------------------------------------------------
  const payloadByRiRef = useRef({});
  const idCacheRef = useRef({});
  const existingMapRef = useRef({});
  const endpointRef = useRef("");
  const endpointBaseRef = useRef("");
  const createdIdsRef = useRef({});
  const snapshotsRef = useRef([]);
  const pushIdRef = useRef(null);
  const baseByRiRef = useRef({});      // ri -> endpoint base used for undo/View-in-FMX links
  const abortRef = useRef(null);       // AbortController for cancelling the batched push

  // Build the batch sub-request for one row (or a pre-flight failure entry).
  // Handles parent-token endpoints, update/delete id resolution, and the Work
  // Request assignment chain. Returns { item } or { fail }. Used by both the bulk
  // push and the per-row retry path, so there is a single request-shaping path.
  const buildBatchItem = ri => {
    const row = mappedRows[ri];
    const name = rowName(row, ri);
    const payload = { ...payloadByRiRef.current[ri] }; // clone — parent-token fields get deleted below
    let reqEndpoint = endpointRef.current;

    const parentTokens = { "{equipmentID}": "equipmentID", "{inventoryID}": "inventoryID" };
    for (const [token, field] of Object.entries(parentTokens)) {
      if (reqEndpoint.includes(token)) {
        const parentId = payload?.[field];
        if (!parentId) return { fail: { ri, name, status: "failed", error: `Missing ${field} reference — required for ${schemaType}`, kind: "dependency" } };
        reqEndpoint = reqEndpoint.replace(token, parentId);
        delete payload[field];
      }
    }

    let httpMethod = "POST";
    let baseForUndo = reqEndpoint;
    if (pushMode === "update" || pushMode === "delete") {
      const nameVal = String(row["Name"] || row["Title"] || row["Tag"] || "").toLowerCase().trim();
      const existingId = existingMapRef.current[nameVal];
      if (!existingId) return { fail: { ri, name, status: "failed", error: `No existing record found matching "${row["Name"] || row["Title"] || row["Tag"]}"`, kind: "dependency" } };
      reqEndpoint = `${endpointRef.current}/${existingId}`;
      httpMethod = pushMode === "delete" ? "DELETE" : "PUT";
      baseForUndo = endpointRef.current; // base without the trailing id
    }

    const item = { key: ri, endpoint: reqEndpoint, method: httpMethod };
    if (pushMode !== "delete") item.payload = payload;

    // Work Request assignment chaining — runs server-side immediately after a
    // successful create, in the same batch item. Non-fatal if it fails.
    if (pushMode === "create") {
      const baseType = schemaType.split(":")[0];
      const assignConfig = FMX_ASSIGNMENT_FIELDS[baseType];
      const assignedUsersRaw = assignConfig && row[assignConfig.userField];
      if (assignedUsersRaw) {
        const userIds = String(assignedUsersRaw).split(/[;,]/).map(s => s.trim()).filter(Boolean)
          .map(nm => idCacheRef.current[`${assignConfig.userField}:${nm}`]).filter(Boolean);
        if (userIds.length > 0) {
          const assignPayload = { assignedUsers: userIds };
          const priorityVal = row[assignConfig.priorityField];
          if (priorityVal) assignPayload.priorityLevel = String(priorityVal);
          item.chain = { endpointTemplate: `${endpointRef.current}/{id}/assignments`, method: "POST", payload: assignPayload };
        }
      }
    }

    baseByRiRef.current[ri] = baseForUndo;
    return { item };
  };

  // Convert one batch-proxy result into a results-table entry.
  const toEntry = result => {
    const ri = result.key;
    const name = rowName(mappedRows[ri], ri);
    if (result.ok) {
      const recId = result.body?.id ?? null;
      if (recId != null) createdIdsRef.current[ri] = recId;
      if (!endpointBaseRef.current && baseByRiRef.current[ri]) endpointBaseRef.current = baseByRiRef.current[ri];
      if (result.chain && !result.chain.ok) console.warn(`Assignment chain failed for row ${ri}:`, result.chain.body);
      return { ri, name, status: pushMode === "update" ? "updated" : "created", id: recId };
    }
    const errorMsg = fmxErrorMessage(result.body, result.status);
    return { ri, name, status: "failed", error: errorMsg || "Unknown error", kind: failKind(errorMsg) };
  };

  useEffect(() => {
    if (phase !== "pushing") return;
    cancelledRef.current = false;
    startRef.current = Date.now();
    setResults([]);
    setDismissedFails(new Set());
    createdIdsRef.current = {};
    snapshotsRef.current = [];
    endpointBaseRef.current = "";
    baseByRiRef.current = {};
    abortRef.current = new AbortController();

    (async () => {
      const endpoint = resolveEndpoint(schemaType, fmxModules);
      endpointRef.current = endpoint;
      const dynFieldMap = allFields?.length ? deriveFieldMap(allFields) : null;
      const dynLookups = allFields?.length ? deriveLookupFields(allFields) : null;
      const dynTypeMap = {};
      if (allFields?.length) for (const f of allFields) { if (f.type && f.type !== "string") dynTypeMap[f.name] = f.type; }

      setPrepMsg("Resolving reference IDs…");
      let idCache = {};
      try {
        const depCaches = projectId ? await getAllDependencyCaches(projectId) : [];
        const result = await buildIdCache(pushIdx.map(ri => mappedRows[ri]), schemaType, creds, depCaches, dynLookups);
        idCache = result.idCache;
      } catch { /* unresolved refs fail per-row below */ }
      if (cancelledRef.current) return;
      idCacheRef.current = idCache;

      setPrepMsg("Fetching field constraints…");
      let sysFields = systemFieldMetadata || [];
      let cfMeta = customFieldMetadata || [];
      try {
        const freshOpts = await fetchPostOptions(creds, schemaType, fmxModules);
        if (freshOpts.systemFields.length > 0) sysFields = freshOpts.systemFields;
        if (freshOpts.customFields.length > 0) cfMeta = freshOpts.customFields;
      } catch { /* cached metadata is fine */ }
      if (cancelledRef.current) return;

      setPrepMsg("Validating payloads…");
      const cfIdMap = customFieldIdMap || {};
      const cfKey = getCustomFieldsPayloadKey(getBaseSchemaType(schemaType));
      const invalid = {};
      payloadByRiRef.current = {};
      for (const ri of pushIdx) {
        const p = transformRowToPayload(mappedRows[ri], schemaType, idCache, cfIdMap, cfMeta, dynFieldMap, dynLookups, dynTypeMap);
        applyDefaults(p, sysFields, cfMeta, cfKey);
        payloadByRiRef.current[ri] = p;
        if (pushMode !== "delete") {
          const { errors } = validatePayload(p, sysFields, cfMeta, cfKey);
          if (errors.length > 0) invalid[ri] = errors.join("; ");
        }
      }

      if (pushMode === "update" || pushMode === "delete") {
        setPrepMsg("Fetching existing records…");
        try {
          const sample = payloadByRiRef.current[pushIdx[0]];
          const nameKey = sample?.tag !== undefined ? "tag" : sample?.title !== undefined ? "title" : "name";
          const records = await fetchAllRecords(creds, endpoint, `id,${nameKey}`);
          const map = {};
          for (const r of records) {
            const key = String(r[nameKey] || "").toLowerCase().trim();
            if (key) map[key] = r.id;
          }
          existingMapRef.current = map;
        } catch (e) { console.warn("Could not fetch existing records:", e); }
      }
      if (cancelledRef.current) return;

      // Update mode: snapshot every target record up-front in one batched set of
      // GETs, so the push phase never blocks on a per-row GET and undo can restore
      // the originals. Snapshots are best-effort — a miss doesn't block the PUT.
      if (pushMode === "update") {
        setPrepMsg("Snapshotting records for undo…");
        const ids = [...new Set(
          pushIdx.map(ri => {
            const r = mappedRows[ri];
            const nameVal = String(r["Name"] || r["Title"] || r["Tag"] || "").toLowerCase().trim();
            return existingMapRef.current[nameVal];
          }).filter(Boolean)
        )];
        try {
          const snaps = await fmxBatchAll({
            projectId,
            items: ids.map(id => ({ key: id, endpoint: `${endpoint}/${id}`, method: "GET" })),
            signal: abortRef.current.signal,
          });
          snapshotsRef.current = snaps.filter(r => r.ok && r.body?.id != null).map(r => ({ id: r.body.id, body: r.body }));
        } catch (e) { console.warn("Snapshot prefetch failed (undo may be limited):", e); }
      }
      if (cancelledRef.current) return;
      setPrepMsg("");

      // Build all sub-requests up front; report pre-flight failures immediately.
      const allEntries = [];
      const items = [];
      for (const ri of pushIdx) {
        if (invalid[ri]) {
          allEntries.push({ ri, name: rowName(mappedRows[ri], ri), status: "failed", error: invalid[ri], kind: failKind(invalid[ri]) });
          continue;
        }
        const built = buildBatchItem(ri);
        if (built.fail) allEntries.push(built.fail);
        else items.push(built.item);
      }
      if (allEntries.length) setResults(prev => [...prev, ...allEntries]);

      // Push through the batch proxy: chunks sent serially, results flushed to
      // the UI one chunk at a time (instead of one re-render per row). Cancel
      // takes effect between chunks — the in-flight chunk still completes and is
      // reported (its rows may already be live in FMX).
      try {
        await fmxBatchAll({
          projectId,
          items,
          chunkSize: 25,
          signal: abortRef.current.signal,
          onChunk: chunkResults => {
            const entries = chunkResults.map(toEntry);
            for (const e of entries) allEntries.push(e);
            setResults(prev => [...prev, ...entries]);
          },
        });
      } catch (e) {
        // Whole-chunk transport/credential failure (the proxy itself errored).
        // Mark any row we never got a result for as failed so the done-phase
        // Retry UI can recover it, then fall through to the done phase.
        const reported = new Set(allEntries.map(en => en.ri));
        const stranded = items
          .filter(it => !reported.has(it.key))
          .map(it => ({ ri: it.key, name: rowName(mappedRows[it.key], it.key), status: "failed", error: e?.message || "Network error", kind: "data" }));
        if (stranded.length) {
          for (const en of stranded) allEntries.push(en);
          setResults(prev => [...prev, ...stranded]);
        }
      }

      const elapsed = Date.now() - startRef.current;
      setDurationMs(elapsed);
      setDoneAt(new Date());

      // Persist the push so it can be undone later from Push History.
      const createdIds = createdIdsRef.current;
      const snapshots = snapshotsRef.current;
      const canUndo = (pushMode === "create" && Object.keys(createdIds).length > 0 && supportsMode(schemaType, "delete"))
        || (pushMode === "update" && snapshots.length > 0 && supportsMode(schemaType, "update"));
      if (canUndo && projectId) {
        try {
          pushIdRef.current = await savePush(projectId, {
            schemaType, mode: pushMode, siteUrl,
            endpointBase: endpointBaseRef.current || endpoint,
            createdIds: pushMode === "create" ? createdIds : null,
            snapshots: pushMode === "update" ? snapshots : null,
            rowCount: pushIdx.length,
            succeeded: allEntries.filter(e => e.status !== "failed").length,
            failed: allEntries.filter(e => e.status === "failed").length,
          });
        } catch (e) { console.warn("Failed to persist push record:", e); }
      }
      // Show the done phase immediately; persistence (which includes a slow
      // best-effort pattern-hint call) and the push-outcome write follow behind.
      setPhase("done");
      await runPersistence();
      await persistPushOutcome(allEntries);
    })();
  }, [phase]);

  const retryRow = async ri => {
    setRetrying(r => ({ ...r, [ri]: true }));
    const built = buildBatchItem(ri);
    let entry;
    if (built.fail) {
      entry = built.fail;
    } else {
      try {
        const [result] = await fmxBatchAll({ projectId, items: [built.item] });
        entry = result ? toEntry(result) : { ri, name: rowName(mappedRows[ri], ri), status: "failed", error: "No response", kind: "data" };
      } catch (e) {
        entry = { ri, name: rowName(mappedRows[ri], ri), status: "failed", error: e?.message || "Network error", kind: "data" };
      }
    }
    setResults(prev => prev.map(e => (e.ri === ri ? { ...entry, retried: true } : e)));
    setRetrying(r => { const n = { ...r }; delete n[ri]; return n; });
  };

  const startUndo = async () => {
    const total = pushMode === "create" ? Object.keys(createdIdsRef.current).length : snapshotsRef.current.length;
    if (!total) return;
    const verb = pushMode === "create" ? "delete" : "revert";
    if (!window.confirm(`This will ${verb} ${total} record${total === 1 ? "" : "s"} in FMX. Continue?`)) return;
    setUndoState({ running: true, done: 0, total });
    const push = {
      mode: pushMode,
      endpoint_base: endpointBaseRef.current || resolveEndpoint(schemaType, fmxModules),
      created_ids: createdIdsRef.current,
      update_snapshots: snapshotsRef.current,
    };
    const result = await executePushUndo(push, creds, ({ done }) => setUndoState(s => ({ ...s, done })));
    if (pushIdRef.current) { try { await markPushUndone(pushIdRef.current, result); } catch { /* non-fatal */ } }
    setUndoState({ result });
  };

  // ---- derived counts ---------------------------------------------------------
  const n = results.length;
  const created = results.filter(e => e.status === "created").length;
  const updated = results.filter(e => e.status === "updated").length;
  const fails = results.filter(e => e.status === "failed");
  const failedLeft = fails.filter(e => !dismissedFails.has(e.ri)).length;

  // Surface the failed count to the wizard chrome (compact-bar badge).
  useEffect(() => {
    onFailedCount?.(phase === "done" ? failedLeft : 0);
    return () => onFailedCount?.(0);
  }, [phase, failedLeft]);
  const liveCount = created + updated;
  const successes = results.filter(e => e.status !== "failed");
  const pct = pushTotal ? Math.round((n / pushTotal) * 100) : 0;
  const elapsedSec = (Date.now() - startRef.current) / 1000;
  const rate = n > 3 ? n / Math.max(1, elapsedSec) : 1.5;
  const etaMin = Math.max(1, Math.ceil((pushTotal - n) / rate / 60));
  const recordUrl = id => {
    if (!id || !siteUrl) return null;
    const base = (endpointBaseRef.current || resolveEndpoint(schemaType, fmxModules)).replace(/^\/v1\//, "");
    return `https://${siteUrl.replace(/^https?:\/\//, "")}/${base}/${id}`;
  };
  const entityUrl = () => {
    if (!siteUrl) return null;
    const base = resolveEndpoint(schemaType, fmxModules).replace(/^\/v1\//, "").split("/")[0];
    return `https://${siteUrl.replace(/^https?:\/\//, "")}/${base}`;
  };
  const verb = pushMode === "update" ? "Update" : pushMode === "delete" ? "Delete" : "Push";

  const viewLink = id => {
    const url = recordUrl(id);
    return url
      ? <a href={url} target="_blank" rel="noreferrer" style={{ font: `600 12px/16px ${T.fontSans}`, color: T.brand, textDecoration: "none", display: "inline-flex", alignItems: "center", gap: 5, whiteSpace: "nowrap" }}>View in FMX<i className="fa-solid fa-up-right-from-square" style={{ fontSize: 9 }} /></a>
      : <span style={{ font: `400 12px ${T.fontSans}`, color: T.inkSoft }}>pushed</span>;
  };

  const statusBadge = s => s === "created"
    ? <Badge color="green" icon="circle-check">Created</Badge>
    : s === "updated"
      ? <Badge color="blueDark" icon="rotate">Updated</Badge>
      : <Badge color="red" icon="circle-exclamation">Failed</Badge>;

  // ---- render -------------------------------------------------------------------
  return (
    <div style={{ width: "100%", maxWidth: 1280, margin: "0 auto" }}>
      {/* Status strip */}
      <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusTable, padding: "14px 18px", display: "flex", alignItems: "center", gap: 14, marginBottom: 14 }}>
        <span style={{ width: 34, height: 34, borderRadius: T.radiusPill, background: T.brand, color: T.white, display: "inline-flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
          <i className={`fa-solid fa-${phase === "ready" ? "cloud-arrow-up" : phase === "pushing" ? "spinner fa-spin" : "flag-checkered"}`} style={{ fontSize: 14 }} />
        </span>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ font: `700 15px/20px ${T.fontSans}`, color: T.ink }}>
            {phase === "ready" && <>{pushTotal} of {totalRows} rows are ready to push{siteUrl ? ` to ${siteUrl}` : ""}.</>}
            {phase === "pushing" && <>{prepMsg || `Pushed ${n} of ${pushTotal}${siteUrl ? ` to ${siteUrl}` : ""}…`}</>}
            {phase === "done" && <>Push complete — {liveCount} of {pushTotal} rows are live in FMX.</>}
          </div>
          <div style={{ font: `400 13px/18px ${T.fontSans}`, color: T.inkMuted, marginTop: 2 }}>
            {phase === "ready" && (holdBack && flaggedIdx.length > 0
              ? <><b style={{ color: T.orangeText }}>{flaggedIdx.length} flagged row{flaggedIdx.length === 1 ? "" : "s"} held back</b> — saved to flagged_rows.csv.{skipped.size > 0 && ` ${skipped.size} skipped row${skipped.size === 1 ? "" : "s"} stay in your file.`}</>
              : <>{skipped.size > 0 ? `${skipped.size} row${skipped.size === 1 ? " was" : "s were"} skipped in earlier steps — they stay in your file. ` : ""}Nothing is written to FMX until you push.</>)}
            {phase === "pushing" && <>Failed rows are reported individually and never block the rest.</>}
            {phase === "done" && (failedLeft > 0
              ? <><b style={{ color: T.red500 }}>{failedLeft} row{failedLeft === 1 ? "" : "s"} failed</b> — fix and retry below, or download the error report.</>
              : <span style={{ color: T.greenDark }}>Every attempted row made it.{skipped.size > 0 && ` The ${skipped.size} skipped row${skipped.size === 1 ? "" : "s"} stayed in your file.`}</span>)}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ display: "flex", height: 8, borderRadius: T.radiusPill, overflow: "hidden", width: 220, background: T.bg }}>
            {phase === "ready" && (
              <>
                <div style={{ flex: Math.max(1, pushTotal), background: T.green500 }} />
                {holdBack && flaggedIdx.length > 0 && <div style={{ flex: flaggedIdx.length * 4, background: T.orange500, marginLeft: 2 }} />}
                {skipped.size > 0 && <div style={{ flex: Math.max(8, skipped.size * 4), background: T.border, marginLeft: 2 }} />}
              </>
            )}
            {phase === "pushing" && <div style={{ width: `${pct}%`, background: T.green500, transition: "width 120ms linear" }} />}
            {phase === "done" && (
              <>
                <div style={{ flex: Math.max(1, liveCount), background: T.green500 }} />
                {failedLeft > 0 && <div style={{ width: Math.min(70, failedLeft * 14), background: T.red500, marginLeft: 2, flexShrink: 0 }} />}
              </>
            )}
          </div>
          <div style={{ font: `500 12px/16px ${T.fontSans}`, color: T.inkMuted, marginTop: 6, display: "flex", gap: 10, justifyContent: "flex-end" }}>
            {phase === "ready" && (
              <>
                <span><i className="fa-solid fa-circle" style={{ fontSize: 7, color: T.green500, marginRight: 5 }} />{pushTotal} ready</span>
                {holdBack && flaggedIdx.length > 0 && <span><i className="fa-solid fa-circle" style={{ fontSize: 7, color: T.orange500, marginRight: 5 }} />{flaggedIdx.length} held back</span>}
                {skipped.size > 0 && <span><i className="fa-solid fa-circle" style={{ fontSize: 7, color: T.border, marginRight: 5 }} />{skipped.size} skipped</span>}
              </>
            )}
            {phase === "pushing" && (
              <>
                <span><i className="fa-solid fa-circle" style={{ fontSize: 7, color: T.green500, marginRight: 5 }} />{created} created</span>
                <span><i className="fa-solid fa-circle" style={{ fontSize: 7, color: T.brand, marginRight: 5 }} />{updated} updated</span>
                <span><i className="fa-solid fa-circle" style={{ fontSize: 7, color: T.red500, marginRight: 5 }} />{fails.length} failed</span>
              </>
            )}
            {phase === "done" && (
              <>
                <span><i className="fa-solid fa-circle" style={{ fontSize: 7, color: T.green500, marginRight: 5 }} />{liveCount} live</span>
                {failedLeft > 0 && <span><i className="fa-solid fa-circle" style={{ fontSize: 7, color: T.red500, marginRight: 5 }} />{failedLeft} failed</span>}
              </>
            )}
          </div>
        </div>
      </div>

      {/* ---- READY ---- */}
      {phase === "ready" && flaggedIdx.length > 0 && (
        <div style={{ background: holdBack ? T.white : T.tint.warning, border: `1px solid ${holdBack ? T.green500 : T.orange500}`, borderRadius: T.radiusCard, padding: "12px 16px", display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap", marginBottom: 14 }}>
          <i className={`fa-solid fa-${holdBack ? "circle-check" : "triangle-exclamation"}`} style={{ fontSize: 14, color: holdBack ? T.green500 : T.orange500, flexShrink: 0 }} />
          <div style={{ flex: 1, minWidth: 240 }}>
            <div style={{ font: `600 13.5px/18px ${T.fontSans}`, color: T.ink }}>
              {holdBack
                ? `${flaggedIdx.length} flagged row${flaggedIdx.length === 1 ? "" : "s"} held back — saved to flagged_rows.csv`
                : `${flaggedIdx.length} row${flaggedIdx.length === 1 ? " still has" : "s still have"} unresolved errors`}
            </div>
            <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkMuted, marginTop: 1 }}>
              {holdBack
                ? "They won't be pushed. Fix them separately and push them later."
                : "Hold them back to push only what's expected to work — they download as a CSV you can work on and push later."}
            </div>
          </div>
          {holdBack && (
            <span style={{ font: `500 11px/16px ${T.fontData}`, color: T.inkMuted, background: T.bg, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusPill, padding: "2px 9px", flexShrink: 0 }}>
              <i className="fa-solid fa-file-csv" style={{ marginRight: 5 }} />flagged_rows.csv · {flaggedIdx.length} row{flaggedIdx.length === 1 ? "" : "s"}
            </span>
          )}
          {holdBack
            ? (
              <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                <Button variant="neutral" size="sm" icon="download" onClick={downloadFlagged}>Download again</Button>
                <Button variant="neutral" size="sm" icon="rotate-left" onClick={() => setHoldBack(false)}>Include them after all</Button>
              </div>
            )
            : <Button variant="secondary" size="sm" icon="file-csv" onClick={() => { setHoldBack(true); downloadFlagged(); }} style={{ flexShrink: 0, background: T.white }}>Remove from push &amp; download CSV</Button>}
        </div>
      )}

      {phase === "ready" && (
        <div style={{ display: "flex", flexWrap: "wrap", gap: 14 }}>
          <div style={{ ...CARD, flex: "1 1 300px", padding: 18 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6 }}>
              <span style={{ width: 32, height: 32, borderRadius: 6, background: T.bgMuted, color: T.inkMuted, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><i className="fa-solid fa-download" /></span>
              <h3 style={{ font: `700 16px ${T.fontSans}`, margin: 0, color: T.ink }}>Download the cleaned file</h3>
            </div>
            <p style={{ font: `400 13px/19px ${T.fontSans}`, color: T.inkMuted, margin: "0 0 14px" }}>
              Hand it to the customer or import manually through FMX's importer. Every mapping, rule, and fix is baked in.
            </p>
            <div style={{ display: "flex", gap: 8 }}>
              <Button variant="neutral" icon="file-csv" onClick={() => handleDownload("csv")}>CSV</Button>
              <Button variant="neutral" icon="file-excel" onClick={() => handleDownload("xlsx")}>XLSX</Button>
            </div>
          </div>
          <div style={{ ...CARD, flex: "1 1 340px", border: `1px solid ${T.blue200}`, padding: 18, boxShadow: T.shadowSm }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 6, flexWrap: "wrap" }}>
              <span style={{ width: 32, height: 32, borderRadius: 6, background: T.blue50, color: T.brand, display: "inline-flex", alignItems: "center", justifyContent: "center" }}><i className="fa-solid fa-cloud-arrow-up" /></span>
              <h3 style={{ font: `700 16px ${T.fontSans}`, margin: 0, color: T.ink }}>Push directly to FMX</h3>
              {hasCreds && selectedProject?.fmx_connection_verified && <Badge color="green" icon="lock">Credentials verified</Badge>}
            </div>
            <p style={{ font: `400 13px/19px ${T.fontSans}`, color: T.inkMuted, margin: "0 0 12px" }}>
              <b style={{ color: T.ink }}>{pushTotal} rows ready</b>{siteUrl ? <> → {siteUrl}</> : null}. Failed rows are reported individually and never block the rest.
            </p>
            {availableModes.length > 1 && (
              <div style={{ display: "flex", gap: 6, marginBottom: 10 }}>
                {availableModes.map(mode => (
                  <button
                    key={mode}
                    onClick={() => setPushMode(mode)}
                    style={{
                      flex: 1, padding: "6px 0", font: `600 12px ${T.fontSans}`, borderRadius: T.radiusBtn, cursor: "pointer",
                      border: `1px solid ${pushMode === mode ? (mode === "delete" ? T.red500 : T.brand) : T.border}`,
                      background: pushMode === mode ? (mode === "delete" ? T.tint.error : T.blue50) : T.white,
                      color: pushMode === mode ? (mode === "delete" ? T.red500 : T.brand) : T.inkMuted,
                    }}
                  >
                    {mode === "create" ? "Create new" : mode === "update" ? "Update existing" : "Delete records"}
                  </button>
                ))}
              </div>
            )}
            {isIrreversible && (
              <label style={{ display: "flex", alignItems: "flex-start", gap: 8, font: `400 12px/16px ${T.fontSans}`, color: T.redDarker, cursor: "pointer", marginBottom: 10 }}>
                <input type="checkbox" checked={ackIrreversible} onChange={e => setAckIrreversible(e.target.checked)} style={{ width: 14, height: 14, marginTop: 1, flexShrink: 0 }} />
                <span>
                  {pushMode === "delete"
                    ? "Records will be permanently deleted from FMX — I understand this cannot easily be undone."
                    : `FMX can't delete ${schemaType} records via API, so this push can't be reversed from Push History — I understand.`}
                </span>
              </label>
            )}
            {!hasCreds && (
              <p style={{ font: `400 12px/16px ${T.fontSans}`, color: T.orangeText, margin: "0 0 10px" }}>
                <i className="fa-solid fa-lock" style={{ marginRight: 5 }} />No saved FMX credentials — add them in project Settings to push directly.
              </p>
            )}
            <Button
              variant="primary"
              icon="cloud-arrow-up"
              disabled={!hasCreds || pushTotal === 0 || (isIrreversible && !ackIrreversible)}
              onClick={() => setPhase("pushing")}
            >
              {verb} {pushTotal} rows
            </Button>
          </div>
        </div>
      )}

      {/* ---- PUSHING ---- */}
      {phase === "pushing" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <div style={{ ...CARD, padding: 18 }}>
            <div style={{ display: "flex", alignItems: "baseline", gap: 10 }}>
              <span style={{ font: `800 26px ${T.fontData}`, color: T.ink, letterSpacing: "-0.02em" }}>{pct}%</span>
              <span style={{ font: `400 13px ${T.fontSans}`, color: T.inkMuted }}>
                {prepMsg || `${n} of ${pushTotal} · about ${etaMin} min left`}
              </span>
              <div style={{ flex: 1 }} />
              <Button variant="neutral" size="sm" icon="pause" onClick={() => { cancelledRef.current = true; abortRef.current?.abort(); }}>Cancel push</Button>
            </div>
            <div style={{ height: 10, borderRadius: T.radiusPill, background: T.bg, overflow: "hidden", margin: "12px 0 14px" }}>
              <div style={{ height: "100%", width: `${pct}%`, background: T.green500, borderRadius: T.radiusPill, transition: "width 120ms linear" }} />
            </div>
            <div style={{ display: "flex", gap: 16, font: `500 13px/18px ${T.fontSans}`, color: T.inkMuted, flexWrap: "wrap" }}>
              <span><i className="fa-solid fa-circle-check" style={{ color: T.green500, marginRight: 6 }} /><b style={{ color: T.ink }}>{created}</b> created</span>
              <span><i className="fa-solid fa-rotate" style={{ color: T.brand, marginRight: 6 }} /><b style={{ color: T.ink }}>{updated}</b> updated</span>
              <span><i className="fa-solid fa-circle-exclamation" style={{ color: fails.length ? T.red500 : T.inkSoft, marginRight: 6 }} /><b style={{ color: T.ink }}>{fails.length}</b> failed</span>
              <span style={{ marginLeft: "auto", color: T.inkSoft }}>Cancelling stops after the current batch — rows already pushed stay; nothing is rolled back.</span>
            </div>
          </div>

          <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusTable, overflow: "hidden" }}>
            <div style={{ padding: "9px 14px", borderBottom: `1px solid ${T.borderSubtle}`, display: "flex", alignItems: "center", gap: 10 }}>
              <span style={{ font: `700 13px ${T.fontSans}`, color: T.ink }}>Live results</span>
              <span style={{ font: `400 12px ${T.fontSans}`, color: T.inkSoft }}>newest first · each record links to FMX the moment it lands</span>
            </div>
            {results.length === 0 && <div style={{ padding: 14, font: `400 12.5px ${T.fontSans}`, color: T.inkSoft }}>Waiting for the first row…</div>}
            {results.slice(-8).reverse().map((e, i) => (
              <div key={e.ri} style={{ display: "grid", gridTemplateColumns: "52px 1fr auto auto", alignItems: "center", gap: 12, padding: "6px 14px", borderBottom: `1px solid ${T.borderSubtle}`, background: i === 0 ? T.blue50 : T.white, transition: "background 300ms ease-out" }}>
                <span style={{ font: `400 11px/16px ${T.fontData}`, color: T.inkSoft }}>#{e.ri + 1}</span>
                <span style={{ font: `600 12.5px/17px ${T.fontData}`, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.name}</span>
                {statusBadge(e.status)}
                {e.status !== "failed"
                  ? viewLink(e.id)
                  : <span style={{ font: `500 12px/16px ${T.fontSans}`, color: T.red500, whiteSpace: "nowrap" }}>reported below when done</span>}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ---- DONE ---- */}
      {phase === "done" && (
        <div style={{ maxWidth: 980, width: "100%", display: "flex", flexDirection: "column", gap: 14, margin: "4px auto 0" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap" }}>
            <span style={{ font: `700 14px ${T.fontSans}`, color: T.ink }}>Push results</span>
            <Badge color="green" icon="circle-check">Completed {doneAt ? doneAt.toLocaleTimeString([], { hour: "numeric", minute: "2-digit" }) : ""}</Badge>
            <span style={{ font: `400 12px ${T.fontSans}`, color: T.inkSoft }}>
              {Math.floor(durationMs / 60000)}m {Math.round((durationMs % 60000) / 1000)}s · {pushTotal} rows attempted
            </span>
            <div style={{ flex: 1 }} />
            {failedLeft > 0 && (
              <Button variant="primary" size="sm" icon="rotate" onClick={() => fails.filter(e => !dismissedFails.has(e.ri)).forEach(e => retryRow(e.ri))}>
                Retry {failedLeft} failed
              </Button>
            )}
            {fails.length > 0 && (
              <Button variant="neutral" size="sm" icon="download" onClick={() => downloadCSV(`${schemaType.replace(/\s+/g, "_")}_failed_rows.csv`, [...mappedHeaders, "_fmxError"], fails.map(e => ({ ...mappedRows[e.ri], _fmxError: e.error })))}>
                Download error report
              </Button>
            )}
            {((pushMode === "create" && Object.keys(createdIdsRef.current).length > 0 && supportsMode(schemaType, "delete"))
              || (pushMode === "update" && snapshotsRef.current.length > 0 && supportsMode(schemaType, "update"))) && !undoState && (
              <Button variant="neutral" size="sm" icon="rotate-left" onClick={startUndo} style={{ color: T.red500 }}>Undo this push</Button>
            )}
          </div>

          {undoState && (
            <div style={{ ...CARD, padding: "10px 14px", font: `500 13px ${T.fontSans}`, color: undoState.result ? T.greenText : T.inkMuted }}>
              {undoState.result
                ? <><i className="fa-solid fa-circle-check" style={{ marginRight: 6 }} />Undo complete — {undoState.result.reversed} reversed{undoState.result.failed ? `, ${undoState.result.failed} failed` : ""}.</>
                : <><i className="fa-solid fa-spinner fa-spin" style={{ marginRight: 6 }} />Undoing push — {undoState.done} of {undoState.total}…</>}
            </div>
          )}

          <div style={{ display: "flex", flexWrap: "wrap", gap: 12 }}>
            {[
              { v: created, k: "Created", color: T.greenText, bg: T.tint.success },
              { v: updated, k: "Updated", color: T.brand, bg: T.tint.info },
              { v: failedLeft, k: "Failed", color: failedLeft ? T.red500 : T.inkSoft, bg: failedLeft ? T.tint.error : T.bg },
            ].map(t => (
              <div key={t.k} style={{ flex: "1 1 140px", background: t.bg, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusCard, padding: "12px 16px" }}>
                <div style={{ font: `800 22px ${T.fontData}`, color: t.color, letterSpacing: "-0.02em" }}>{t.v}</div>
                <div style={{ font: `600 11px ${T.fontSans}`, color: T.inkMuted, textTransform: "uppercase", letterSpacing: "0.04em", marginTop: 2 }}>{t.k}</div>
              </div>
            ))}
          </div>

          {/* Failed rows */}
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
              <span style={{ ...SECTION_LABEL, color: failedLeft ? T.redDarker : T.greenDark }}>Failed rows</span>
              <span style={{ font: `400 12px/16px ${T.fontSans}`, color: T.inkMuted }}>— fix and retry; retrying never duplicates rows that already made it</span>
            </div>
            <div style={{ background: T.white, border: `1px solid ${failedLeft ? T.red500 : T.green500}`, borderRadius: T.radiusTable, overflow: "hidden" }}>
              {fails.length === 0 && (
                <div style={{ padding: 14, font: `500 13px ${T.fontSans}`, color: T.greenDark, display: "flex", alignItems: "center", gap: 8 }}>
                  <i className="fa-solid fa-circle-check" />No failures — every attempted row made it.
                </div>
              )}
              {fails.map((e, i) => {
                const skippedFail = dismissedFails.has(e.ri);
                const spinning = !!retrying[e.ri];
                return (
                  <div key={e.ri} style={{ borderBottom: i === fails.length - 1 ? "none" : `1px solid ${T.borderSubtle}`, padding: "9px 14px", background: skippedFail ? T.bg : T.tint.error, boxShadow: `inset 2px 0 0 ${skippedFail ? T.border : T.red500}`, transition: "background 120ms ease-out", opacity: skippedFail ? 0.7 : 1 }}>
                    <div style={{ display: "grid", gridTemplateColumns: "15px 150px 1fr auto", alignItems: "center", gap: 10 }}>
                      <i className={`fa-solid fa-${skippedFail ? "ban" : e.kind === "dependency" ? "link-slash" : e.kind === "duplicate" ? "copy" : "circle-exclamation"}`} style={{ fontSize: 12, color: skippedFail ? T.inkSoft : T.red500 }} />
                      <div style={{ minWidth: 0 }}>
                        <div style={{ font: `600 13px/18px ${T.fontSans}`, color: T.ink }}>Row {e.ri + 1}</div>
                        <div style={{ marginTop: 2 }}>
                          <span style={{ font: `500 11px/16px ${T.fontData}`, color: T.ink, background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: 3, padding: "1px 5px" }}>{e.name}</span>
                        </div>
                      </div>
                      <div style={{ minWidth: 0 }}>
                        <div style={{ font: `600 12.5px/17px ${T.fontSans}`, color: T.ink, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                          {!skippedFail && (
                            <span style={{ ...PILL, color: T.redDarker, border: `1px solid ${T.red500}` }}>
                              {e.kind === "dependency" ? "Dependency" : e.kind === "duplicate" ? "Duplicate" : "Error"}
                            </span>
                          )}
                          <span style={{ overflow: "hidden", textOverflow: "ellipsis" }}>{skippedFail ? "Skipped — left out of this push" : e.error}</span>
                        </div>
                        {!skippedFail && (
                          <div style={{ font: `400 11.5px/15px ${T.fontSans}`, color: T.inkMuted, marginTop: 1 }}>
                            <i className="fa-solid fa-wand-magic-sparkles" style={{ fontSize: 9, color: T.brand, marginRight: 5 }} />{failHint(e.kind)}
                          </div>
                        )}
                      </div>
                      <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                        {!skippedFail && (
                          <>
                            <Button variant="primary" size="sm" icon={spinning ? "spinner fa-spin" : "rotate"} onClick={() => !spinning && retryRow(e.ri)}>
                              {spinning ? "Retrying…" : "Retry"}
                            </Button>
                            <Button variant="neutral" size="sm" icon="ban" onClick={() => setDismissedFails(s => new Set([...s, e.ri]))}>Skip</Button>
                          </>
                        )}
                        {skippedFail && (
                          <Button variant="neutral" size="sm" icon="rotate-left" onClick={() => setDismissedFails(s => { const nx = new Set(s); nx.delete(e.ri); return nx; })}>Undo</Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Successful rows */}
          <div>
            <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
              <span style={{ ...SECTION_LABEL, color: T.greenDark }}>Successful rows</span>
              <span style={{ font: `400 12px/16px ${T.fontSans}`, color: T.inkMuted }}>— live in FMX now; same details as the push log</span>
            </div>
            <div style={{ background: T.white, border: `1px solid ${T.green500}`, borderRadius: T.radiusTable, overflow: "hidden" }}>
              <div style={{ maxHeight: 300, overflowY: "auto" }}>
                {successes.length === 0 && <div style={{ padding: 14, font: `400 12.5px ${T.fontSans}`, color: T.inkSoft }}>No rows made it — fix the failures above and retry.</div>}
                {successes.map(e => (
                  <div key={e.ri} style={{ display: "grid", gridTemplateColumns: "52px 1fr auto auto", alignItems: "center", gap: 12, padding: "6px 14px", borderBottom: `1px solid ${T.borderSubtle}`, background: T.white }}>
                    <span style={{ font: `400 11px/16px ${T.fontData}`, color: T.inkSoft }}>#{e.ri + 1}</span>
                    <span style={{ font: `600 12.5px/17px ${T.fontData}`, color: T.ink, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{e.name}{e.retried ? " · pushed on retry" : ""}</span>
                    {statusBadge(e.status)}
                    {viewLink(e.id)}
                  </div>
                ))}
              </div>
              <div style={{ padding: "6px 14px", font: `400 11.5px/15px ${T.fontSans}`, color: T.inkSoft, borderTop: `1px solid ${T.borderSubtle}` }}>
                {successes.length} successful row{successes.length === 1 ? "" : "s"} · scroll for more · each links to its record in FMX
              </div>
            </div>
          </div>

          {/* Summary */}
          <div style={{ background: T.white, border: `1px solid ${T.borderSubtle}`, borderRadius: T.radiusTable, padding: 14 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, font: `600 13px ${T.fontSans}`, color: failedLeft === 0 ? T.green500 : T.red500 }}>
              <i className={`fa-solid fa-${failedLeft === 0 ? "circle-check" : "circle-exclamation"}`} />
              {failedLeft === 0
                ? `${liveCount} rows are live in FMX — ${getDisplayBase(schemaType)} is migrated`
                : `${failedLeft} failed row${failedLeft === 1 ? "" : "s"} remain${failedLeft === 1 ? "s" : ""}`}
            </div>
            <div style={{ font: `400 12px/17px ${T.fontSans}`, color: T.inkMuted, marginTop: 6 }}>
              {failedLeft === 0
                ? <>
                    {holdBack && flaggedIdx.length > 0
                      ? `${flaggedIdx.length} held-back row${flaggedIdx.length === 1 ? " is" : "s are"} in flagged_rows.csv — fix and push them anytime.${skipped.size > 0 ? ` The ${skipped.size} skipped row${skipped.size === 1 ? "" : "s"} stayed in your file.` : ""}`
                      : `${skipped.size > 0 ? `The ${skipped.size} skipped row${skipped.size === 1 ? "" : "s"} stayed in your file. ` : ""}Everything else is on the site.`}
                    {nextEntity && <> Next entity up: <b style={{ color: T.ink }}>{nextEntity}</b>.</>}
                  </>
                : "Retry them above, or finish now — failed rows stay listed on the project and can be retried anytime."}
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              {entityUrl() && (
                <Button variant="secondary" size="sm" icon="up-right-from-square" onClick={() => window.open(entityUrl(), "_blank")} style={{ flex: 1, justifyContent: "center" }}>
                  View {getDisplayBase(schemaType)} in FMX
                </Button>
              )}
              <Button variant="primary" size="sm" icon="flag-checkered" onClick={async () => { await runPersistence(); await persistPushOutcome(results); onFinish?.(); }} style={{ flex: 1, justifyContent: "center" }}>
                Finish — back to workspace
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function getDisplayBase(schemaType) {
  return schemaType ? schemaType.split(":")[0] : "";
}
